"use client";

import { forwardRef, type PointerEventHandler } from "react";

import type { AnimatedIconName } from "@perceptron/ui/components/animations/animation-icons";
import { Button } from "@perceptron/ui/components/button";
import { Icon, IconName } from "@perceptron/ui/components/icon";
import { cn } from "@perceptron/utils/cn";

import { useIconHoverPlayback, type HoverPlayback } from "./useIconHoverPlayback";

type ToggleButtonProps = {
  isActive: boolean;
  className?: string;
  isDisabled?: boolean;
  onClick: () => void;
  label?: string;
  showLabel?: boolean;
} & (
  | { icon: IconName; animated?: undefined }
  /** An animation-capable icon that plays while hovered; see `HoverPlayback` for the exit. */
  | {
      icon: AnimatedIconName;
      animated: HoverPlayback;
      /** Playback rate of the hover animation; `1` is the asset's own pace. */
      iconSpeed?: number;
    }
);

const ToggleButton = forwardRef<HTMLButtonElement, ToggleButtonProps>(
  ({ isActive, isDisabled, onClick, icon, animated, label, showLabel = true, className, ...props }, ref) => {
    const playback = useIconHoverPlayback(animated ?? "finish");
    const iconSpeed = "iconSpeed" in props ? props.iconSpeed : undefined;
    // A tooltip trigger wrapping this button passes its own pointer handlers in; compose rather
    // than replace them, or the tooltip's leave handler would silence ours.
    const {
      onPointerEnter,
      onPointerLeave,
      iconSpeed: _iconSpeed,
      ...rest
    } = props as typeof props & {
      iconSpeed?: number;
      onPointerEnter?: PointerEventHandler<HTMLButtonElement>;
      onPointerLeave?: PointerEventHandler<HTMLButtonElement>;
    };

    return (
      <Button
        aria-label={label}
        className={cn(
          "h-auto rounded-full border-[1.5px] border-solid text-foreground-primary shadow-none label-2",
          // Square without the label, so the hover fill is a disc.
          showLabel && label ? "px-3 py-2" : "size-9 shrink-0 justify-center p-0",
          isActive && !isDisabled
            ? "border-brand-blue bg-blue-faded text-brand-blue hover:bg-blue-faded dark:text-white"
            : "border-transparent bg-background-alt-2 hover:bg-background-tint sm:bg-transparent",
          className,
        )}
        disabled={isDisabled}
        ref={ref}
        onClick={onClick}
        {...rest}
        onPointerEnter={(event) => {
          onPointerEnter?.(event);
          if (animated) playback.onPointerEnter();
        }}
        onPointerLeave={(event) => {
          onPointerLeave?.(event);
          if (animated) playback.onPointerLeave();
        }}
      >
        {animated ? (
          <Icon
            animated
            animationRef={playback.animationRef}
            aria-hidden
            autoplay={false}
            className="size-4"
            loop={false}
            name={icon}
            ref={playback.svgRef}
            speed={iconSpeed}
          />
        ) : (
          <Icon className="size-4" name={icon} />
        )}
        {label && showLabel && <span className="ml-1 mt-[1px]">{label}</span>}
      </Button>
    );
  },
);
ToggleButton.displayName = "ToggleButton";

export default ToggleButton;
